/* eslint-disable */
import { MediaPipeSegmenter } from '../segmentation/MediaPipeSegmenter'
import { BackgroundCompositor } from '../segmentation/BackgroundCompositor'
import { ColorFilterTransformer } from '../transformers/ColorFilterTransformer'
import type { NSFWDetector } from '../nsfw/NSFWDetector'
import type {
  BackgroundFilterType,
  BackgroundBlurConfig,
  ColorFilterConfig,
  VirtualBackgroundConfig,
  VideoBackgroundConfig
} from '../core/types'

const BLUR_RADIUS_MAP: Record<string, number> = {
  low: 10,
  high: 20
}

/**
 * Single transformer that orchestrates background segmentation, color
 * filters, and NSFW sampling in one pipeline pass.
 *
 * Implements the `Transformer` interface expected by
 * `MediaProcessor.setTransformers()` from `@vonage/media-processor`.
 *
 * Frame flow per call to {@link transform}:
 *
 *   1. **Background** — If active, segment the person with
 *      {@link MediaPipeSegmenter} and composite via
 *      {@link BackgroundCompositor}. Produces an intermediate frame.
 *   2. **Color** — If active (and not identity), apply color adjustments
 *      via {@link ColorFilterTransformer} logic onto the intermediate or
 *      original frame. Produces a new intermediate frame.
 *   3. **NSFW** — If active, sample the final frame for classification
 *      (non-blocking, pass-through).
 *
 * The resulting frame is enqueued; all intermediate frames are closed.
 */
export class UnifiedTransformer {
  private segmenter: MediaPipeSegmenter
  private compositor: BackgroundCompositor
  private colorCanvas: OffscreenCanvas | null = null
  private colorCtx: OffscreenCanvasRenderingContext2D | null = null

  private backgroundEnabled = false
  private backgroundType: BackgroundFilterType = null
  private blurRadius = 20

  private colorEnabled = false
  private colorConfig: Required<ColorFilterConfig> = ColorFilterTransformer.normalize({})

  private nsfwEnabled = false
  private nsfwDetector: NSFWDetector | null = null
  private nsfwSampleIntervalMs = 1500
  private nsfwLastSampleTime = 0
  private nsfwAnalyzing = false
  private nsfwCanvas: OffscreenCanvas | null = null
  private nsfwCtx: OffscreenCanvasRenderingContext2D | null = null

  constructor () {
    this.segmenter = new MediaPipeSegmenter()
    this.compositor = new BackgroundCompositor()
  }

  // ─── Transformer interface ──────────────────────────────────────

  async transform (frame: VideoFrame, controller: TransformStreamDefaultController): Promise<void> {
    let current: VideoFrame = frame

    try {
      // Stage 1: Background segmentation + compositing
      if (this.backgroundEnabled && this.segmenter.isLoaded()) {
        const mask = this.segmenter.segment(current)
        if (mask) {
          let composed: VideoFrame | null = null

          if (this.backgroundType === 'blur') {
            composed = this.compositor.compositeBlur(current, mask, this.blurRadius)
          } else if (this.backgroundType === 'virtual') {
            composed = this.compositor.compositeImage(current, mask)
          } else if (this.backgroundType === 'video') {
            composed = this.compositor.compositeVideo(current, mask)
          }

          if (composed) {
            current.close()
            current = composed
          }
        }
      }

      // Stage 2: Color filters
      if (this.colorEnabled && !ColorFilterTransformer.isIdentity(this.colorConfig)) {
        const filtered = this.applyColorFilter(current)
        if (filtered) {
          current.close()
          current = filtered
        }
      }

      // Stage 3: NSFW sampling (non-blocking pass-through)
      if (this.nsfwEnabled && this.nsfwDetector) {
        this.maybeSampleNSFW(current)
      }

      controller.enqueue(current)
    } catch (e) {
      if (current !== frame) {
        try { current.close() } catch (_) {}
      }
      try {
        controller.enqueue(frame)
      } catch (_) {
        try { frame.close() } catch (_) {}
      }
    }
  }

  // ─── Background controls ───────────────────────────────────────

  async enableBackground (
    type: BackgroundFilterType,
    config?: BackgroundBlurConfig | VirtualBackgroundConfig | VideoBackgroundConfig
  ): Promise<void> {
    if (!this.segmenter.isLoaded() && !this.segmenter.isLoading()) {
      await this.segmenter.loadModel()
    }

    this.backgroundType = type
    this.backgroundEnabled = type !== null

    if (type === 'blur' && config && 'radius' in config) {
      this.blurRadius = typeof config.radius === 'string'
        ? (BLUR_RADIUS_MAP[config.radius] || 20)
        : config.radius
    }

    if (type === 'virtual' && config && 'imageUrl' in config) {
      await this.compositor.loadBackgroundImage(config.imageUrl)
    }

    if (type === 'video' && config && 'videoUrl' in config) {
      this.compositor.loadBackgroundVideo(config.videoUrl)
    }
  }

  disableBackground (): void {
    this.backgroundEnabled = false
    this.backgroundType = null
  }

  isBackgroundActive (): boolean {
    return this.backgroundEnabled
  }

  getBackgroundType (): BackgroundFilterType {
    return this.backgroundType
  }

  // ─── Color controls ─────────────────────────────────────────────

  enableColor (config: ColorFilterConfig): void {
    this.colorConfig = ColorFilterTransformer.normalize(config)
    this.colorEnabled = true
  }

  disableColor (): void {
    this.colorEnabled = false
    this.colorConfig = ColorFilterTransformer.normalize({})
  }

  isColorActive (): boolean {
    return this.colorEnabled
  }

  getColorConfig (): Required<ColorFilterConfig> {
    return { ...this.colorConfig }
  }

  // ─── NSFW controls ──────────────────────────────────────────────

  enableNSFW (detector: NSFWDetector, sampleIntervalMs = 1500): void {
    this.nsfwDetector = detector
    this.nsfwSampleIntervalMs = sampleIntervalMs
    this.nsfwEnabled = true
  }

  disableNSFW (): void {
    this.nsfwEnabled = false
    this.nsfwDetector = null
  }

  // ─── Lifecycle ──────────────────────────────────────────────────

  hasAnyFilterActive (): boolean {
    return this.backgroundEnabled || this.colorEnabled || this.nsfwEnabled
  }

  destroy (): void {
    this.segmenter.destroy()
    this.compositor.destroy()
    this.colorCanvas = null
    this.colorCtx = null
    this.nsfwCanvas = null
    this.nsfwCtx = null
    this.backgroundEnabled = false
    this.colorEnabled = false
    this.nsfwEnabled = false
  }

  // ─── Expose sub-components for BackgroundManager ────────────────

  getSegmenter (): MediaPipeSegmenter {
    return this.segmenter
  }

  getCompositor (): BackgroundCompositor {
    return this.compositor
  }

  // ─── Private ────────────────────────────────────────────────────

  private applyColorFilter (frame: VideoFrame): VideoFrame | null {
    const width = frame.displayWidth
    const height = frame.displayHeight

    if (!this.colorCanvas || this.colorCanvas.width !== width || this.colorCanvas.height !== height) {
      this.colorCanvas = new OffscreenCanvas(width, height)
      this.colorCtx = this.colorCanvas.getContext('2d') as OffscreenCanvasRenderingContext2D
    }

    if (!this.colorCtx) return null

    try {
      const ctx = this.colorCtx

      ctx.filter = ColorFilterTransformer.buildCSSFilterString(this.colorConfig)
      ctx.drawImage(frame as any, 0, 0, width, height)

      if (this.colorConfig.temperature !== 0) {
        this.applyTemperature(ctx, width, height)
      }

      return new VideoFrame(this.colorCanvas as any, {
        timestamp: frame.timestamp || 0
      })
    } catch (_) {
      return null
    }
  }

  private applyTemperature (
    ctx: OffscreenCanvasRenderingContext2D,
    w: number,
    h: number
  ): void {
    const intensity = Math.min(Math.abs(this.colorConfig.temperature), 1)
    const alpha = intensity * 0.15

    ctx.save()
    ctx.globalCompositeOperation = 'color'
    ctx.fillStyle = this.colorConfig.temperature > 0
      ? `rgba(255, 147, 41, ${alpha})`
      : `rgba(41, 121, 255, ${alpha})`
    ctx.fillRect(0, 0, w, h)
    ctx.restore()
  }

  private maybeSampleNSFW (frame: VideoFrame): void {
    if (!this.nsfwDetector || !this.nsfwDetector.isModelLoaded()) return
    if (this.nsfwAnalyzing) return

    const now = performance.now()
    if (now - this.nsfwLastSampleTime < this.nsfwSampleIntervalMs) return

    this.nsfwLastSampleTime = now

    try {
      const width = frame.displayWidth
      const height = frame.displayHeight

      if (!this.nsfwCanvas || this.nsfwCanvas.width !== width || this.nsfwCanvas.height !== height) {
        this.nsfwCanvas = new OffscreenCanvas(width, height)
        this.nsfwCtx = this.nsfwCanvas.getContext('2d') as OffscreenCanvasRenderingContext2D
      }

      if (!this.nsfwCtx) return

      this.nsfwCtx.drawImage(frame as any, 0, 0, width, height)

      this.nsfwAnalyzing = true
      this.nsfwDetector.classify(this.nsfwCanvas as any).finally(() => {
        this.nsfwAnalyzing = false
      })
    } catch (_) {
      // Sampling errors must not break the pipeline
    }
  }
}
